import { IATA } from '../iata';

export namespace Emirates {
  type Unknown = any;
  type DateTimestamp = string; // [YYYY-MM-DD HH:MI:SS] Should probably be changed from string

  // Courtesy of https://www.skycargo.com/media/u4jmhu1l/01apr2025_lsc_europe-russia_sweden-se-revised-with-df.pdf
  export enum ProductCode {
    // General
    GCR = 'GCR', // Emirates AirFreight
    AXA = 'AXA', // Emirates AirFreight Priority

    // Perishables, Food
    PXS = 'PXS', // Emirates Fresh
    PXD = 'PXD', // Emirates Fresh Breathe
    PXF = 'PXF', // Emirates Fresh Perishable Frozen

    // Perishable, Non-Food (no expedited comparable)
    PNF = 'PNF', // Emirates Fresh Perishable Non Food

    // Pharma
    AXS = 'AXS', // Emirates Pharma
    AXD = 'AXD', // Emirates Pharma Plus

    // Others
    AXM = 'AXM', // Emirates Medical Devices
    AXT = 'AXT', // Emirates Vital (medical related but not pharmaceuticals)
    VAL = 'VAL', // Emirates Secure VAL
    VUN = 'VUN', // Emirates Secure VUN
    AOG = 'AOG', // aircraft parts

    // DG
    PAX = 'PAX', // Emirates DG-PAX
    CAO = 'CAO', // Emirates DG-CAO
  }

  export type Code<T = string> = {
    code: T;
    description?: string;
    name?: string; // This field is only used in one place
  };

  export type ProductCodeInternal = {
    product: Code<ProductCode>;
  };

  export type Measurement = {
    value: number;
    unit: Code;
  };

  export type Dimension = {
    volume: Measurement;
    width: Measurement;
    length: Measurement;
    height: Measurement;
    weightPerPiece: Measurement;
    weight: Measurement;
    piece: number;
    include?: boolean;
    tiltable?: boolean;
    serialNumber: number;
  };

  export type Quantity = {
    weight: Measurement;
    volume: Measurement;
    piece: number;
    dimension?: Dimension[];
    specialHandling?: Unknown[];
  };

  export type CargoRequest = {
    useAllotment?: boolean;
    quantity: Quantity;
    cargoType: string;
    commodityCode: string;
    goodsDescription: string;
  };

  export type CargoResponse = {
    cargoType: string;
    cargoCategory: string;
    commodityCode: Code;
    quantityInfo: Quantity[];
    goodsDescription: string;
    hamonizedCommodityCode: Unknown[];
  };

  export type DocumentDetails = {
    documentPrefix: string; // N[3]
    documentNumber: string | 'UNK'; // N[8] | UNK
    documentType: 'AWB' | 'CBV' | 'CN38';
  };

  export type Route = {
    originInfo: Code;
    destinationInfo: Code;
    routes: {
      // airport and carrier
      apt1: string;
      apt2?: string;
      apt3?: string;
      apt4?: string;
      apt5?: string;
      apt6?: string;
      apt7?: string;
      apt8?: string;
      apt9?: string;
      apt10?: string;
      apt11?: string;
      apt12?: string;
      apt13?: string;
      apt14?: string;
      apt15?: string;
      apt16?: string;
      apt17?: string;
      apt18?: string;
      apt19?: string;
      apt20: string;
      carrier1?: string;
      carrier2?: string;
      carrier3?: string;
      carrier4?: string;
      carrier5?: string;
      carrier6?: string;
      carrier7?: string;
      carrier8?: string;
      carrier9?: string;
      carrier10?: string;
      carrier11?: string;
      carrier12?: string;
      carrier13?: string;
      carrier14?: string;
      carrier15?: string;
      carrier16?: string;
      carrier17?: string;
      carrier18?: string;
      carrier19?: string;
      carrier20?: string;
    };
  };

  export type Account = {
    type: string;
    number: string;
    accId?: string;
    default?: boolean;
  };

  export type Participant = {
    type: string;
    name?: string;
    account: Account[];
    identifier?: string;
    address?: Unknown[];
    otherIdentifier?: Unknown[];
  };

  export type ChargeDeclarationRequest = {
    currency: string;
    mop: {
      chargeCode: string;
      weightValuation: string;
      otherCharges: string;
    };
    declaredValue: {
      carriage: Money;
    };
  };

  export type ChargeDeclarationResponse = {
    currency: Code;
    mop: {
      chargeCode: Code;
      weightValuation: Code;
      otherCharge: Code;
    };
    declaredValue: {
      carriage?: Money;
    };
  };

  export type Vehicle = {
    mode: Code;
    body: Code;
    type?: Code;
    registrationNumber?: string;
  };

  export type Transport = {
    carrier: string;
    number: string;
    extensionNumber?: string;
    vehicle?: Vehicle;
  };

  export type DateTime = {
    schedule: DateTimestamp;
    estimated: DateTimestamp;
  };

  export type ItineraryRequest = {
    transportInfo: Transport;
    boardPoint: string;
    offPoint: string;
    allotmentId: string;
    departureDateTimeLocal: DateTime;
    arrivalDateTimeLocal: DateTime;
  };

  export type ItineraryResponse = {
    boardPoint: Code;
    offPoint: Code;
    transportInfo: Transport;
    departureDateTimeLocal: DateTime;
    departureDateTimeUTC: DateTime;
    arrivalDateTimeLocal: DateTime;
    arrivalDateTimeUTC: DateTime;
    legNumber: number;
    oalIndicator: boolean;
    quantity: Quantity;
    allotmentId: string;
    journeyTime: string;
    pickupTimeLocal: DateTime;
    readyTimeLocal: DateTime;
    addonInfo: Unknown[];
  };

  export type Currency = {
    origin: Code;
    destination: Code;
    requested: Code;
    base: Code;
  };

  export type Exchange = {
    fromCurrency: string;
    toCurrency: string;
    rate: number;
  };

  export type Money = {
    amount: number;
    currency: string;
  };

  export type OtherCharge = {
    otherChargeId: string;
    applicability: Code;
    category: string;
    city: Code;
    code: Code;
    due: string;
    exchangeRate: number;
    mop: Code;
    type: string;
    value: Money;
    userInput: boolean;
  };

  export type WeightType = {
    ratingWeight: Measurement;
    grossWeight: Measurement;
    chargeableWeight: Measurement;
  };

  export type Charge = {
    status: Code;
    ratePerUnit: Money;
    freightCharge: {
      amount: number;
      currency: string;
      rateId: string;
      rateSource: string;
      mop: Code;
      commission: Money;
      discount: Money;
    };
    netCharge: Money;
    valuationCharge: {
      value: Money;
      mop: Code;
    };
    otherCharge: OtherCharge[];
  };

  export type Price = {
    currency: Currency;
    exchange: Exchange[];
    requestedSpotRate: Unknown; // Empty Object
    offerId: string;
    weightType: {
      ratingWeight: Measurement;
      grossWeight: Measurement;
      chargeableWeight: Measurement;
    };
    charge: {
      hurdle: Charge;
      applicable: Charge;
      requested: Charge;
    };
    grossRate: Unknown[]; // Empty Array
    ancillaryCharges: Unknown[]; // Empty Array
    additionalInfo: Unknown[]; // Empty Array
  };

  export type Product = {
    airCapacity: {
      documentInfo: DocumentDetails;
      origin: Code;
      destination: Code;
      pickupDrop: Code;
      routes: Unknown[]; // Literally an empty array
      participant: Participant[];
      cargoInfo: CargoResponse;
      product: ProductCodeInternal;
      offerItinerary: {
        journeyTime: string;
        noOfStops: string;
        route: string;
        itinerary: ItineraryResponse[];
        staffIndicator: boolean;
      };
      chargeDeclaration: ChargeDeclarationResponse;
      capacityAncillaries: Unknown[];
      ancillaryCompletion: Unknown[];
      embargo: Unknown[];
      accountingInfo: Unknown[];
    };
  };

  export type OfferFilter = {
    airCapacityFilter: {
      documentInfo: DocumentDetails;
      routeInfo?: Route;
      cargoInfo: CargoRequest;
      participantInfo?: Participant[];
      productInfo: ProductCodeInternal;
      chargeDeclarationInfo: ChargeDeclarationRequest;
      scheduleInfo: {
        shipByDate: DateTimestamp;
        itineraryFilter: ItineraryRequest[];
      };
    };
  };

  export type Page = {
    pageNumber: number;
    pageSize: number;
    totalElements: number;
  };

  export type Sort = {
    sortBy: string;
    sortOrder: string;
  };

  export type Data = {
    offers: {
      offer: Offer[];
    };
    upsellOffers: {
      upselloffer: Unknown[];
    };
    requestIdentifier: string;
    pageInfo: Page;
    sortInfo: Sort;
    type: string;
  };

  export type Offer = {
    priceInfo: Price;
    productInfo: Product;
  };

  export type APIResponse = {
    data: Data;
    status: string;
  };

  export type ParticipantParams = {
    type: 'AGT' | 'SHP' | 'CNE';
    companyName: string;
    contactName?: string;
    name?: string;
    address: {
      addressType: 'B' | 'C'; // business, communication
      place: string;
      country: {
        code: string; // A[2]
        description?: string;
      };
      city?: {
        code?: string; // A[3]
        description?: string;
      };
      poBox?: string;
      addressLine1: string;
      addressLine2?: string;
      addressLine3?: string;
      stateProvince?: string;
      locationCode?: string;
      communication?: {
        type: 'EMAIL' | 'MOB' | 'FX' | 'SMS' | 'TE';
        value: string;
      }[];
    }[];
    account: {
      type: 'IATA' | 'INTERNAL';
      number: string;
    }[];
    identifier?: string; // required in lieu of account
  };

  export type QuantityInfoParams = {
    piece: number;
    tiltable?: boolean;
    stackable?: boolean;
    include?: boolean;
    specialHandling?: {
      code: string; // AN[3]
      description?: string; // AN[30]
      category: IATA.HandlingCode;
      // 'ONL' | 'DGR' | 'PER' | 'AVI' | 'VAL' | 'FRZ' | 'LIV' | 'HUM' | 'BUL' | 'SPE'; // ONL=Online, DGR=Dangerous Goods, PER=Perishable, AVI=Live Animals, VAL=Valuables, FRZ=Frozen Goods, LIV=Live Fish, HUM=Human Remains, BUL=Bulky Goods, SPE=Special Goods
    }[]; // Max 2, A[3]
    weight: {
      unit: { code: 'K' | 'L' };
      value: number;
    };
    volume: {
      unit: { code: 'CM' | 'CC' | 'CF' | 'CI' }; // Cubic Meter, Cubic Centimeter, Cubic Feet, Cubic Inch
      value: number; // N[3,6]
    };
    dimension?: {
      volume: {
        unit: { code: 'CM' | 'CC' | 'CF' | 'CI' };
        value: number; // N[3,6]
      };
      width: {
        value: number; // N[4,2]
        unit?: { code: 'CM' };
      };
      length: {
        value: number; // N[4,2]
        unit?: { code: 'CM' };
      };
      height: {
        value: number; // N[4,2]
        unit?: { code: 'CM' };
      };
    }[];
  };

  export type CargoInfoParams = {
    cargoType: 'AWB' | 'CBV' | 'CN38';
    cargoCategory: 'F' | 'C' | 'M'; // Freight, Courier, Mail
    goodsDescription: string;
    commodityCode: {
      code: string; // IATA commodity code or user defined
      description?: string;
    };
    quantityInfo: QuantityInfoParams[];
  };

  export type ItineraryParam = {
    boardPoint: {
      code: string; // AN[3]
    };
    offPoint: {
      code: string; // AN[3]
    };
    transportInfo: Transport;
    allotmentId?: string;
    departureDateTimeLocal?: {
      schedule: string; // YYYY-MM-DD HH:MM:SS
      estimated?: string; // YYYY-MM-DD HH:MM:SS
    };
    arrivalDateTimeLocal?: {
      schedule: string; // YYYY-MM-DD HH:MM:SS
      estimated?: string; // YYYY-MM-DD HH:MM:SS
    };
    arrivalDateTimeUTC?: {
      schedule: string; // YYYY-MM-DD HH:MM:SS
      estimated?: string; // YYYY-MM-DD HH:MM:SS
    };
    departureDateTimeUTC?: {
      schedule: string; // YYYY-MM-DD HH:MM:SS
      estimated?: string; // YYYY-MM-DD HH:MM:SS
    };
    legNumber?: number;
    oalIndicator?: boolean;
    quantity?: QuantityInfoParams;
    journeyTime?: string; // XX Hour(s) YY Minute(s)
    pickupTimeLocal?: {
      schedule: string; // YYYY-MM-DD HH:MM:SS
      estimated?: string; // YYYY-MM-DD HH:MM:SS
    };
    readyTimeLocal?: {
      schedule: string; // YYYY-MM-DD HH:MM:SS
      estimated?: string; // YYYY-MM-DD HH:MM:SS
    };
  };

  export type OrderItemParams = {
    priceInfo?: {
      offerId?: string;
      currency: {
        origin: {
          code: string; // A[3]
        };
        destination: {
          code: string; // A[3]
        };
        requested: {
          code: string; // A[3]
        };
        base: {
          code: string; // A[3]
        };
      };
      exchange?: {
        fromCurrency: string; // A[3]
        toCurrency: string; // A[3]
        rate: number;
      }[];
      weightType: {
        ratingWeight?: {
          value: number;
          unit: { code: 'K' | 'L' };
        };
        grossWeight: {
          value: number;
          unit: { code: 'K' | 'L' };
        };
        chargeableWeight: {
          value: number;
          unit: { code: 'K' | 'L' };
        };
      };
      charge: {
        hurdle: {
          status: {
            code: string; // AN[3]
            description?: string;
          };
          ratePerUnit: {
            amount: number;
            currency: string;
          };
          freightCharge: {
            amount: number;
            currency: string;
            rateId?: string;
            rateSource?: string;
            mop: {
              code: string; // AN[3]
              description?: string;
            };
            commission?: {
              amount: number;
              currency: string;
            };
            discount?: {
              amount: number;
              currency: string;
            };
          };
          netCharge: {
            amount: number;
            currency: string;
          };
          valuationCharge?: {
            value: {
              amount: number;
              currency: string;
            };
            mop: {
              code: string; // AN[3]
              description?: string;
            };
          };
          otherCharge?: {
            otherChargeId?: string;
            applicability: {
              code: string; // AN[3]
              description?: string;
            };
            category: string; // AN[3]
            city: {
              code: string; // A[3]
              description?: string;
            };
            code: {
              code: string; // AN[3]
              description?: string;
            };
            exchangeRate?: number;
            mop: {
              code: string; // AN[3]
              description?: string;
            };
            type: string; // AN[3]
            value: {
              amount: number;
              currency: string;
            };
            userInput: boolean;
            due: string; // AN[3]
          }[];
        };
        applicable: {
          status: {
            code: string; // AN[3]
            description?: string;
          };
          ratePerUnit: {
            amount: number;
            currency: string;
          };
          freightCharge: {
            amount: number;
            currency: string;
            rateId?: string;
            rateSource?: string;
            mop: {
              code: string; // AN[3]
              description?: string;
            };
            commission?: {
              amount: number;
              currency: string;
            };
            discount?: {
              amount: number;
              currency: string;
            };
          };
          netCharge: {
            amount: number;
            currency: string;
          };
          valuationCharge?: {
            value: {
              amount: number;
              currency: string;
            };
            mop: {
              code: string; // AN[3]
              description?: string;
            };
          };
          otherCharge?: {
            otherChargeId?: string;
            applicability: {
              code: string; // AN[3]
              description?: string;
            };
            category: string; // AN[3]
            city: {
              code: string; // A[3]
              description?: string;
            };
            code: {
              code: string; // AN[3]
              description?: string;
            };
            exchangeRate?: number;
            mop: {
              code: string; // AN[3]
              description?: string;
            };
            type: string; // AN[3]
            value: {
              amount: number;
              currency: string;
            };
            userInput: boolean;
            due: string; // AN[3]
          }[];
        };
        requested: {
          status: {
            code: string; // AN[3]
            description?: string;
          };
          ratePerUnit: {
            amount: number;
            currency: string;
          };
          freightCharge: {
            amount: number;
            currency: string;
            rateId?: string;
            rateSource?: string;
            mop: {
              code: string; // AN[3]
              description?: string;
            };
            commission?: {
              amount: number;
              currency: string;
            };
            discount?: {
              amount: number;
              currency: string;
            };
          };
          netCharge: {
            amount: number;
            currency: string;
          };
          valuationCharge?: {
            value: {
              amount: number;
              currency: string;
            };
            mop: {
              code: string; // AN[3]
              description?: string;
            };
          };
          otherCharge?: OtherCharge[];
        };
      };
    };
    productInfo: {
      airCapacity: {
        documentInfo: DocumentDetails;
        origin: {
          code: string; // A[3]
          name?: string;
        };
        destination: {
          code: string; // A[3]
          name?: string;
        };
        shipByDate: string; // YYYY-MM-DD HH:MM:SS
        participant: ParticipantParams[];
        cargoInfo: CargoInfoParams;
        product: {
          product: {
            code: string; // AN[5]
            description?: string; // AN[30]
            name?: string; // AN[30]
          };
          service?: {
            code: string; // AN[2]
            description?: string; // AN[30]
          };
        };
        chargeDeclaration: {
          currency: {
            code: string; // A[3]
            description?: string;
          };
          mop: {
            weightValuation: {
              code: 'P' | 'C'; // prepaid, collect
              description?: 'P' | 'C'; // prepaid, collect
            };
            otherCharge: {
              code: 'P' | 'C'; // prepaid, collect
              description?: 'P' | 'C'; // prepaid, collect
            };
            chargeCode: {
              code: 'PP' | 'CC' | 'PC' | 'CP'; // etc??
              description?: 'PP' | 'CC' | 'PC' | 'CP'; // etc??
            };
          };
          declaredValue?: {
            carriage?: {
              amount: number;
              currency: string;
            };
            insurance?: {
              amount: number;
              currency: string;
            };
            customs?: {
              amount: number;
              currency: string;
            };
          };
        };
        offerItinerary: {
          journeyTime?: string; // XX Hour(s) YY Minute(s)
          route?: string; // e.g. "JFK-DXB-LHR"
          noOfStops?: string;
          itinerary: ItineraryParam[];
          staffIndicator?: boolean;
        };
        deliveryByDate?: string; // YYYY-MM-DD HH:MM:SS
        vasInd?: boolean;
      };
    };
    confirm: boolean;
    versionId?: number;

    remark?: {
      remark: string;
      type: 'USR' | 'OSI' | 'SSR' | 'CNCL';
      station?: string; // A[3]
    };
  };

  // Most minimal required fields + those that we may want to use
  export type CreateOrderParams = {
    orderItems: {
      orderItem: OrderItemParams[];
    };
    action: 'CREATE' | 'CHANGE' | 'CANCEL';
    orderNumber?: number;
  };

  export type CreateOrderResponse = any;

  /**
   * Every EK endpoint we call, keyed the way {@link AuthenticatedClient} reads a type map: by method,
   * then by path relative to the gateway base URL. `{orderId}`/`{bookingId}` are filled from the
   * call's `pathParams`.
   *
   * Only `actions/create` declares a body: the update, cancel and offer payloads we actually send
   * carry fields the request types above do not describe yet (`reference`, a repeated `remark`, an
   * `offerFilter` envelope), so those routes stay on the untyped `unknown` body until the DTOs catch
   * up with the wire.
   */
  export type Api = {
    GET: {
      '/predict/order/services/cargo/v1/orders/o{orderId}': {
        request: { pathParams: { orderId: string | number }; params: { timezone: string } };
        response: CreateOrderResponse;
      };
    };
    POST: {
      '/predict/order/services/cargo/v1/actions/create': {
        request: { body: CreateOrderParams };
        response: CreateOrderResponse;
      };
      '/predict/order/services/cargo/v1/orders/b{bookingId}': {
        request: { pathParams: { bookingId: string | number } };
        response: CreateOrderResponse;
      };
      '/predict/order/services/cargo/v1/offers': { response: APIResponse };
    };
    PUT: {
      '/predict/order/services/cargo/v1/orders/b{bookingId}': {
        request: { pathParams: { bookingId: string | number } };
        response: CreateOrderResponse;
      };
    };
  };
}
